/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX} from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { defaultAddressFields } from '../../shared/cardConfig/constants';
import { CONTROL_TYPE } from '../../utils/constants';
import AttachmentList from '../AttachmentList';
import Label from '../Label';
import { Wrapper } from '../ReviewField';

const Text = styled.p`
  color: var(--neutral-1000);
  font: var(--sprout-font-body1-bold);
  min-height: 1.5em;
`;

const FieldLabel = styled(Label)`
  color: var(--neutral-500);
  font: var(--sprout-font-body2-bold);
`;

export function getUseAddress(value: any, store: any): any {
  if (value?.requestType === 'Sold To') {
    return value;
  }

  if (value?.useBillTo) {
    return getUseAddress(store.customer.billTo.address, store);
  } else if (value?.useSoldTo) {
    return getUseAddress(store.customer.soldTo.address, store);
  } else if (value?.usePayer) {
    return getUseAddress(store.customer.payer.address, store);
  }

  return value;
}

const getAddressReadonlyValue = (
  address: any,
  field: any,
  t: (key: string) => string
): string => {
  let displayValue = address?.[field.field] ?? '';

  if (field.type === CONTROL_TYPE.checkbox) {
    displayValue = address?.[field.field]
      ? t('messages.yes')
      : t('messages.no');
  }

  return displayValue;
};


interface AddressReadonlyProps {
  address: any;
  label?: string;
  addressFields?: any[];
  value: any;
  store: any;
  showDistrict?: boolean;
}

export function AddressReadonly({
  address,
  label,
  addressFields = [],
  value,
  store,
  showDistrict
}: AddressReadonlyProps): JSX.Element {
  const { t } = useTranslation();
  const addressToUse = getUseAddress(value, store);

  const nonDefaultAddressFields = addressFields
    .map(field => {
      if (
        !defaultAddressFields.find(
          i => i.field === field.field
        )
      ) {
        const textValue = getAddressReadonlyValue(
          address,
          field,
          t
        );

        return (
          <Wrapper key={field.label}>
            <FieldLabel>{field.label}</FieldLabel>
            <Text>
              {textValue === '' ? <br /> : textValue}
            </Text>
          </Wrapper>
        );
      }
      return undefined;
    })
    .filter(Boolean);

  return (
    <Fragment>
      {nonDefaultAddressFields}

      {label && <FieldLabel>{label}</FieldLabel>}

      <Text>{addressToUse?.street || '--'}</Text>

      <Text>
        {`${addressToUse?.city || '--'} ${
          addressToUse?.state || '--'
        }${
          showDistrict
            ? ` ${addressToUse?.district || '--'}`
            : ''
        } ${addressToUse?.zipCode || '--'} ${
          addressToUse?.country || '--'
        }`}
      </Text>
    </Fragment>
  );
}

interface RequiredGroupReadonlyProps {
  value: any;
  requiredGroupFields: any[];
}

export function RequiredGroupReadonly({
  value,
  requiredGroupFields
}: RequiredGroupReadonlyProps): JSX.Element {
  return (
    <Fragment>
      {requiredGroupFields.map(item => {
        const itemValue = value?.[item.field];

        return (
          <Fragment
            key={`${item.type}-${itemValue}`}
          >
            <FieldLabel>{item.label || ''}</FieldLabel>
            <ReadonlyField
              type={item.type}
              value={itemValue}
            />
          </Fragment>
        );
      })}
    </Fragment>
  );
}

interface InputWithTagsReadonlyProps {
  value?: string[];
}

export function InputWithTagsReadonly({
  value
}: InputWithTagsReadonlyProps): JSX.Element {
  if (!value || value.length === 0) {
    return <Text>--</Text>;
  }

  return (
    <Fragment>
      {value.map(tag => (
        <Text key={tag}>{tag}</Text>
      ))}
    </Fragment>
  );
}

interface ReadonlyFieldProps {
  type: string;
  value: any;
  label?: string;
  addressFields?: any[];
  requiredGroupFields?: any[];
  attachments?: any[];
  store?: any;
}

export function ReadonlyField({
  type,
  value,
  label,
  addressFields,
  requiredGroupFields,
  attachments,
  store = {},
}: ReadonlyFieldProps): JSX.Element {
  const { t } = useTranslation();
  const {
    customer: { region = '', id: hcId = '' } = {}
  } = store;

  switch (type) {
    case CONTROL_TYPE.checkbox:
      return (
        <Text>
          {value ? t('messages.yes') : t('messages.no')}
        </Text>
      );

    case CONTROL_TYPE.address:
      return (
        <AddressReadonly
          value={value}
          label={label}
          address={value}
          addressFields={addressFields}
          store={store}
          showDistrict={
            region.toLowerCase() === 'latam'
          }
        />
      );

    case CONTROL_TYPE.password: {
      const valueStr = (value || '').toString();
      const hidden = valueStr.length - 4;
      const text =
        hidden > 0
          ? '*'.repeat(hidden) +
            valueStr.substring(hidden)
          : valueStr;

      return <Text>{text || '--'}</Text>;
    }

    case CONTROL_TYPE.requiredGroup:
      return (
        <RequiredGroupReadonly
          value={value}
          requiredGroupFields={
            requiredGroupFields || []
          }
        />
      );

    case CONTROL_TYPE.attachmentList:
      return (
        <AttachmentList
              label={label}
              attachments={attachments}
              hcId={hcId}
              isReadonly
              handleRemove={() => {}}
              handleDownload={() => {}}
            />
      );

    case CONTROL_TYPE.inputWithTags:
      return (
        <InputWithTagsReadonly value={value} />
      );

    case CONTROL_TYPE.radio:
    case CONTROL_TYPE.text:
    default:
      return <Text>{value || '--'}</Text>;
  }
}